import {
  Fragment,
  type FormEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { isProblemError } from '../../api/client';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  getListConversationsQueryKey,
  getListMessagesQueryKey,
  listMessages,
  useCreateAttachmentDownloadURL,
  useCreateAttachmentUpload,
  useGetAttachmentPolicy,
  useGetConversation,
  useListConversations,
  useMarkMessageRead,
  useSendMessage,
} from '../../api/generated/messaging/messaging';
import type { Attachment, Me, Message, Notification } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
// A mesma pilha de cursores do cadastro e das gravacoes: cada pagina devolve
// `nextCursor` e nada devolve cursor de volta. Uma segunda pilha aqui seria um
// segundo lugar onde "voltar" pode discordar.
import { usePager } from '../enrollment/AdminOnly';
// Erro e vazio nunca juntos: a regra mora num lugar so, e nao em cada tela.
import { ListOutcome } from '../enrollment/ListOutcome';
import {
  formatDateInPersonTimezone,
  formatNumber,
  formatTimeInPersonTimezone,
} from '../../i18n/datetime';
import styles from './messaging.module.css';
import { uploadToSignedUrl } from './signedUpload';
import { POLL_INTERVAL_MS, useNotificationStream } from './useNotificationStream';

/**
 * Caixa de mensagens.
 *
 * Tres decisoes de tela que vem direto do contrato:
 *
 * 1. **409 e 403 nao sao a mesma recusa.** Caixa fechada por vinculo encerrado
 *    responde 409 e preserva a leitura; quem nao e participante responde 403.
 *    A tela desabilita o envio nos dois casos e escreve motivos diferentes ao
 *    lado do campo — colapsa-los deixaria a pessoa sem saber se o problema e
 *    dela ou do vinculo.
 * 2. **Nao existe criar conversa.** Ela nasce do vinculo, entao a tela lista e
 *    abre, e nunca oferece "nova conversa": a rota nao existe justamente para
 *    que ninguem tente o identificador de outra pessoa.
 * 3. **Tempo real e degradavel.** Se o fluxo SSE cai, a tela volta a consultar
 *    por intervalo em vez de ficar muda.
 */
export function Inbox() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const meQuery = useGetMe();
  const me = meQuery.data;
  const { conversationId } = useParams();

  const [live, setLive] = useState<Notification | null>(null);
  const openConversationId = useRef<string | undefined>(conversationId);

  useEffect(() => {
    openConversationId.current = conversationId;
  }, [conversationId]);

  const onNotification = useCallback(
    (notification: Notification) => {
      if (notification.kind !== 'message_received') return;
      // O aviso e regiao viva; a navegacao usa `targetId`, que existe no evento
      // exatamente para nao obrigar uma consulta so para descobrir o destino.
      setLive(notification);
      void queryClient.invalidateQueries({ queryKey: getListConversationsQueryKey() });
      const open = openConversationId.current;
      if (open) void queryClient.invalidateQueries({ queryKey: getListMessagesQueryKey(open) });
    },
    [queryClient],
  );

  const streamStatus = useNotificationStream(onNotification);
  const polling = streamStatus === 'polling';

  /**
   * A barra lateral tambem e paginada por cursor.
   *
   * `ListConversations` ordena pela ultima atividade, DESCENDENTE, e trunca a
   * pagina (`messaging/store/queries.sql`). Pedir sem cursor e ler `items` dava
   * as conversas mais recentes e nenhum caminho ate as outras: quem tem mais de
   * uma pagina de vinculos nao alcancava a conversa de quem nao escreve ha um
   * mes — e nada na tela dizia que a lista acabava antes dos vinculos.
   */
  const pager = usePager();
  const conversations = useListConversations(pager.cursor === undefined ? {} : { cursor: pager.cursor }, {
    query: {
      enabled: !!me,
      refetchInterval: polling ? POLL_INTERVAL_MS : false,
    },
  });

  useEffect(() => {
    if (!polling) return;
    // Queda do fluxo: consulta na hora, e a partir dai por intervalo. Esperar o
    // primeiro tique deixaria a caixa desatualizada justamente no minuto em que
    // o tempo real falhou.
    void queryClient.invalidateQueries({ queryKey: getListConversationsQueryKey() });
  }, [polling, queryClient]);

  if (!me) return null;

  const items = conversations.data?.items ?? [];
  const nextCursor = conversations.data?.nextCursor ?? null;

  return (
    <div className={styles.page}>
      <h1>{t('messaging.title')}</h1>

      <p className={styles.announce} role="status" aria-live="polite">
        {live ? (
          <>
            <span>{live.subject}</span>
            {live.targetId ? (
              <Link className={styles.announceLink} to={`/messages/${live.targetId}`}>
                {t('messaging.openConversation')}
              </Link>
            ) : null}
          </>
        ) : null}
      </p>

      {polling ? <Alert tone="info">{t('messaging.streamDegraded')}</Alert> : null}
      <div className={styles.layout}>
        <nav className={styles.sidebar} aria-label={t('messaging.conversations')}>
          {/*
            O estado da lista de conversas mora dentro da lista de conversas. O
            alerta ficava acima do `layout`, longe do lugar vazio que ele
            explica, e os dois apareciam somados: "nao foi possivel carregar" e
            "voce ainda nao tem conversas" na mesma tela, sendo que a segunda e
            uma afirmacao sobre os vinculos que ninguem conseguiu verificar.
          */}
          <ListOutcome
            isPending={conversations.isPending}
            isError={conversations.isError}
            count={items.length}
            empty={t('messaging.empty')}
          />
          <ul className={styles.conversationList}>
            {items.map((conversation) => (
              <li key={conversation.id}>
                <Link
                  className={styles.conversationLink}
                  to={`/messages/${conversation.id}`}
                  aria-current={conversation.id === conversationId ? 'page' : undefined}
                >
                  <span className={styles.counterpart}>{conversation.counterpart.displayName}</span>
                  {conversation.unreadCount > 0 ? (
                    <span className={styles.unread}>
                      {/*
                        `count`, e nao `total`: e o nome que dispara a regra de
                        plural do i18next. Com outro nome a chave nunca escolhe
                        entre `_one` e `_other`, e a conversa com uma mensagem
                        nova exibia "1 nao lidas".
                      */}
                      {t('messaging.unread', { count: conversation.unreadCount })}
                    </span>
                  ) : null}
                  {conversation.state === 'closed_for_sending' ? (
                    <span className={styles.closedTag}>{t('messaging.closedTag')}</span>
                  ) : null}
                  {conversation.lastMessageAt ? (
                    <span className={styles.meta}>
                      {formatDateInPersonTimezone(conversation.lastMessageAt, me.timezone, me.locale)}
                    </span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>

          {/*
            Os dois controles existem mesmo na unica pagina, desabilitados: um
            botao que so aparece quando ha mais some justamente quando alguem
            procura por ele. O rotulo vem do catalogo de cadastro porque e o
            mesmo par de frases neutras — ver o relatorio, `fronteira`.
          */}
          <div className={styles.pager}>
            <Button disabled={!pager.canGoBack} onClick={pager.goBack}>
              {t('enrollment.previousPage')}
            </Button>
            <Button
              disabled={nextCursor === null}
              onClick={() => {
                if (nextCursor !== null) pager.goForward(nextCursor);
              }}
            >
              {t('enrollment.nextPage')}
            </Button>
          </div>
        </nav>

        {conversationId ? (
          <Thread key={conversationId} me={me} conversationId={conversationId} polling={polling} />
        ) : (
          <section className={styles.thread} aria-label={t('messaging.threadRegion')}>
            <p>{t('messaging.pickConversation')}</p>
          </section>
        )}
      </div>
    </div>
  );
}

/**
 * Limites que o contrato define para os campos desta tela.
 *
 * Escritos aqui, e nao deixados por conta do servidor: **nao ha validador de
 * contrato no caminho da requisicao** (api/internal/app), entao corpo maior que
 * o limite nao volta 422 — e gravado, e a leitura seguinte devolve um campo que
 * viola o esquema do qual este proprio cliente foi gerado. Quem falha primeiro,
 * entao, e o consumidor gerado, e nao quem escreveu demais.
 *
 * `maxLength` no controle e o unico ponto em que a pessoa descobre o limite
 * enquanto ainda pode fazer algo a respeito.
 */
const MESSAGE_BODY_MAX_LENGTH = 10_000;
const ATTACHMENT_TITLE_MAX_LENGTH = 200;

/**
 * Tamanho de pagina do historico: o `maximum` que o contrato declara para
 * `limit`.
 *
 * Nao e otimizacao — e a diferenca entre uma conversa de 64 mensagens abrir na
 * mensagem certa e abrir na 50a. `listMessages` e ASCENDENTE com LIMIT 50 por
 * omissao (`messaging/store/queries.sql`), entao a primeira pagina traz as mais
 * ANTIGAS e as mais novas ficam atras do cursor. Pedir o maximo por pagina
 * reduz as idas ate o fim; quem garante que se chega ao fim e o cursor.
 */
const MESSAGE_PAGE_LIMIT = 200;

/**
 * Lado da conversa a que uma mensagem pertence.
 *
 * Nao ha terceiro valor nem ausencia. E por isso que `MessageRow` recebe `side`
 * como propriedade obrigatoria em vez de receber a conversa inteira e decidir
 * sozinha: linha sem lado deixa de compilar, e era exatamente esse o estado que
 * a caixa exibia — recebida e enviada na mesma coluna, com o mesmo fundo.
 */
type Side = 'outgoing' | 'incoming';

interface ThreadEntry {
  readonly message: Message;
  readonly side: Side;
  /** Verdadeiro so na primeira nao lida: e ali, e so ali, que o separador entra. */
  readonly opensUnread: boolean;
}

/** A fronteira de nao lidas, do jeito que ela estava quando a conversa abriu. */
interface UnreadBoundary {
  /** @nullable Nulo quando nao havia nada por ler. */
  readonly firstUnreadId: string | null;
  readonly total: number;
}

/**
 * Ordena do mais antigo para o mais recente.
 *
 * A ordem e decidida **aqui**, e nao herdada da resposta. Duas razoes: a tela
 * ancora na ultima posicao da lista para abrir na mensagem mais recente, e a
 * paginacao por cursor ainda vai chegar — quando chegar, a pagina pode muito
 * bem vir do mais novo para o mais velho, e nada nesta tela pode depender disso
 * para saber qual e a ultima.
 */
function chronological(messages: readonly Message[]): readonly Message[] {
  return [...messages].sort((left, right) =>
    left.sentAt === right.sentAt
      ? left.id.localeCompare(right.id)
      : left.sentAt.localeCompare(right.sentAt),
  );
}

function unreadBoundaryOf(ordered: readonly Message[], viewerId: string): UnreadBoundary {
  const unread = ordered.filter(
    (message) => message.sender.personId !== viewerId && message.readAt === null,
  );
  return { firstUnreadId: unread[0]?.id ?? null, total: unread.length };
}

function threadEntries(
  ordered: readonly Message[],
  viewerId: string,
  boundary: UnreadBoundary,
): readonly ThreadEntry[] {
  return ordered.map((message) => ({
    message,
    side: message.sender.personId === viewerId ? 'outgoing' : 'incoming',
    opensUnread: message.id === boundary.firstUnreadId,
  }));
}

/**
 * Uma conversa aberta.
 *
 * Tres decisoes de leitura, todas com a mesma origem — a caixa abria na
 * mensagem mais antiga e o campo de resposta ficava depois de todo o historico:
 *
 * 1. **A lista rola, a tela nao.** O historico e o proprio recipiente de
 *    rolagem, com altura limitada; o campo de resposta fica fora dele e por
 *    isso alcancavel sem percorrer conversa nenhuma.
 * 2. **Abre na mais recente.** Ancorar na primeira nao lida foi considerado e
 *    recusado: com 43 por ler, ancorar no meio do historico devolve o campo de
 *    resposta para fora da tela, que e o defeito que se esta consertando. O
 *    separador marca onde a pessoa parou; a ancora fica no fim.
 * 3. **A tela nao rola sozinha depois disso.** Puxar para o fim a cada consulta
 *    arrancaria do lugar quem subiu para reler. So um envio proprio reancora.
 */
function Thread({
  me,
  conversationId,
  polling,
}: {
  readonly me: Me;
  readonly conversationId: string;
  readonly polling: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const bodyId = useId();
  const reasonId = useId();

  const conversationQuery = useGetConversation(conversationId);
  /**
   * O historico INTEIRO, e nao a primeira pagina dele.
   *
   * A caixa abre na mensagem mais recente — e essa e a decisao 2 do bloco acima.
   * So que `listMessages` e ascendente com LIMIT: sem cursor, "a ultima da
   * lista" era a 50a mensagem, e as mais novas — as unicas que alguem abre a
   * conversa para ler — nao tinham por onde ser alcancadas. Nao ha leitura
   * decrescente no contrato, entao chegar ao fim e seguir `nextCursor` ate ele
   * vir nulo.
   *
   * A chave e a mesma de `getListMessagesQueryKey(conversationId)`, sem
   * parametros: e ela que `invalidateQueries` usa depois de enviar e depois de
   * marcar como lida, e uma chave com o `limit` dentro deixaria as duas
   * invalidando uma consulta que ninguem observa.
   */
  const messagesQuery = useInfiniteQuery({
    queryKey: getListMessagesQueryKey(conversationId),
    queryFn: ({ pageParam, signal }) =>
      listMessages(
        conversationId,
        pageParam === undefined
          ? { limit: MESSAGE_PAGE_LIMIT }
          : { limit: MESSAGE_PAGE_LIMIT, cursor: pageParam },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: polling ? POLL_INTERVAL_MS : false,
  });

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = messagesQuery;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada, e nao e detalhe:
    // sem ele, a pagina que falha no meio do historico deixa `hasNextPage`
    // verdadeiro e `isFetchingNextPage` de volta em falso, o efeito dispara de
    // novo e a caixa entra em tempestade de requisicoes contra o servidor que
    // acabou de recusar.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);
  const send = useSendMessage();
  const markRead = useMarkMessageRead();
  /**
   * `mutateAsync`, e nao `mutate`.
   *
   * As duas disparam a mesma operacao, mas o observador de mutacao guarda os
   * callbacks de `mutate` num **unico** campo: marcar tres mensagens no mesmo
   * efeito sobrescreve os dois primeiros `onSettled`, e so o ultimo roda. Uma
   * contagem apoiada neles nunca chega a zero, e a invalidacao que deveria vir
   * depois da ultima marcacao simplesmente nao sai. A promessa devolvida por
   * chamada nao tem esse problema: ela e capturada no ato.
   */
  const markMutate = markRead.mutateAsync;

  const [body, setBody] = useState('');
  const [pending, setPending] = useState<readonly Attachment[]>([]);
  /** `closed` vem do 409; `forbidden`, do 403. Sao motivos diferentes. */
  const [denied, setDenied] = useState<'closed' | 'forbidden' | null>(null);
  /**
   * Recusa que nao e 409 nem 403 — 5xx, 404 de conversa removida, queda de
   * rede, resposta que nem chega a ser Problem.
   *
   * Precisa de estado proprio, e nao de mais um valor em `denied`: as duas
   * recusas do contrato **fecham a caixa** (o campo desabilita e o motivo fica
   * ao lado dele), e esta nao fecha nada — a mensagem continua escrita e o
   * caminho e tentar de novo. Sem este sinal o botao apenas parava de girar, e
   * quem escreveu nao sabia se a mensagem tinha saido.
   */
  const [sendFailed, setSendFailed] = useState(false);
  const markedRef = useRef<Set<string>>(new Set());

  const pages = messagesQuery.data?.pages;
  const loaded = useMemo(() => pages?.flatMap((page) => page.items), [pages]);
  const messages = useMemo(() => chronological(loaded ?? []), [loaded]);
  /**
   * O historico so aparece quando ele acabou de chegar.
   *
   * Ancorar numa lista que ainda vai crescer e ancorar na mensagem errada — e a
   * ancora e usada UMA vez, entao a pagina seguinte chegaria com a caixa parada
   * no meio do historico. A fronteira de nao lidas tem o mesmo problema: ela
   * nasce com o componente, e nascer sobre meia conversa marcaria como "primeira
   * nao lida" uma mensagem que talvez nem seja.
   */
  const complete = loaded !== undefined && !hasNextPage;
  /** Cada incremento manda o historico voltar a ancorar na mensagem mais recente. */
  const [anchorRequest, setAnchorRequest] = useState(0);

  useEffect(() => {
    const novas: string[] = [];
    for (const message of messages) {
      if (message.sender.personId === me.id) continue;
      if (message.readAt !== null) continue;
      if (markedRef.current.has(message.id)) continue;
      markedRef.current.add(message.id);
      novas.push(message.id);
    }
    if (novas.length === 0) return;

    // Marcar como lida muda duas leituras que a tela ja mostra — o selo de nao
    // lidas na barra lateral e o `readAt` do historico — e nenhuma das duas se
    // atualiza sozinha. Sem esta invalidacao o selo continua contando as
    // mensagens que a pessoa esta lendo neste instante, ate que outro evento
    // invalide a lista por acaso.
    //
    // Uma invalidacao **depois da ultima** marcacao, e nao uma por mensagem:
    // abrir uma conversa com 43 por ler dispararia 43 consultas da lista de
    // conversas para chegar ao mesmo resultado.
    //
    // `allSettled`, e nao `all`: a marcacao que falha nao pode segurar a
    // invalidacao das outras — o que ja foi lido continua tendo de sair do
    // contador.
    void Promise.allSettled(novas.map((id) => markMutate({ messageId: id }))).then(() => {
      void queryClient.invalidateQueries({ queryKey: getListConversationsQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getListMessagesQueryKey(conversationId) });
    });
  }, [messages, me.id, markMutate, queryClient, conversationId]);

  const conversation = conversationQuery.data;
  const closed = conversation?.state === 'closed_for_sending' || denied === 'closed';
  const forbidden = denied === 'forbidden' || (isProblemError(conversationQuery.error) && conversationQuery.error.status === 403);
  const canSend = !closed && !forbidden;
  const reason = closed
    ? t('messaging.closedReason')
    : forbidden
      ? t('messaging.forbiddenReason')
      : null;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!canSend || body.trim().length === 0) return;

    setSendFailed(false);
    send.mutate(
      {
        conversationId,
        data: { body: body.trim(), attachmentIds: pending.map((attachment) => attachment.id) },
      },
      {
        onSuccess: () => {
          setBody('');
          setPending([]);
          // Reancora: quem acabou de escrever quer ver o que escreveu, e este e
          // o unico movimento de rolagem que nao pega a pessoa de surpresa.
          setAnchorRequest((request) => request + 1);
          void queryClient.invalidateQueries({
            queryKey: getListMessagesQueryKey(conversationId),
          });
          void queryClient.invalidateQueries({ queryKey: getListConversationsQueryKey() });
        },
        onError: (error) => {
          // O contrato separa os dois de proposito, e a tela precisa preservar
          // a diferenca ate o texto ao lado do campo.
          if (isProblemError(error) && error.status === 409) {
            setDenied('closed');
            return;
          }
          if (isProblemError(error) && error.status === 403) {
            setDenied('forbidden');
            return;
          }
          // Todo o resto cai aqui, inclusive o que nem e Problem. Antes havia
          // um `return` antecipado para erro nao-Problem: falha de rede — o
          // caso mais comum de todos — saia da tela sem deixar sinal nenhum.
          setSendFailed(true);
        },
      },
    );
  };

  if (conversationQuery.isError && !forbidden) {
    return (
      <section className={styles.thread} aria-label={t('messaging.threadRegion')}>
        <Alert tone="error">{t('error.loadFailed')}</Alert>
      </section>
    );
  }

  return (
    <section className={styles.thread} aria-label={t('messaging.threadRegion')}>
      <h2>{conversation ? conversation.counterpart.displayName : t('messaging.threadRegion')}</h2>

      {complete ? (
        <MessageHistory me={me} messages={messages} anchorRequest={anchorRequest} />
      ) : null}
      {complete && messages.length === 0 ? <p>{t('messaging.noMessages')}</p> : null}
      {/*
        Historico que nao fechou. Enquanto `complete` for falso nada do
        historico e desenhado — e uma leitura que falhou no meio nunca fecha.
        Sem esta linha a conversa aparecia so com o campo de resposta, muda,
        como se nao houvesse mensagem nenhuma. O envio continua liberado: nao
        conseguir LER o historico nao impede escrever.
      */}
      {!complete && (messagesQuery.isError || isFetchNextPageError) ? (
        <Alert tone="error">{t('error.loadFailed')}</Alert>
      ) : null}

      <form className={styles.composer} onSubmit={submit}>
        <label className={styles.label} htmlFor={bodyId}>
          {t('messaging.messageLabel')}
        </label>
        <textarea
          id={bodyId}
          className={styles.textarea}
          value={body}
          disabled={!canSend}
          maxLength={MESSAGE_BODY_MAX_LENGTH}
          aria-describedby={reason ? reasonId : undefined}
          onChange={(event) => setBody(event.target.value)}
        />
        {reason ? (
          <p id={reasonId} className={styles.reason}>
            {reason}
          </p>
        ) : null}

        {/*
          A falha terminal fica **junto do campo**, e nao no topo da pagina: e
          ali que o texto continua escrito, e e dali que a pessoa decide se
          tenta de novo. O texto diz que a mensagem nao saiu, porque "erro
          inesperado" deixa exatamente a duvida que importa — se a outra ponta
          recebeu e nao respondeu, ou se nunca recebeu.
        */}
        {sendFailed ? <Alert tone="error">{t('messaging.sendFailed')}</Alert> : null}

        <AttachmentComposer
          locale={me.locale}
          blocked={!canSend}
          attached={pending}
          onAttached={(attachment) => setPending((current) => [...current, attachment])}
        />

        <Button
          type="submit"
          variant="primary"
          loading={send.isPending}
          disabled={!canSend || body.trim().length === 0}
        >
          {t('messaging.send')}
        </Button>
      </form>
    </section>
  );
}

/**
 * O historico de uma conversa aberta.
 *
 * Componente proprio por uma razao concreta: ele so e montado depois de a
 * primeira pagina chegar, e por isso a fronteira de nao lidas pode **nascer com
 * ele**. Recalcula-la a cada consulta apagaria o separador no instante seguinte
 * — a propria tela marca como lida toda recebida que exibe, entao `readAt`
 * deixa de ser nulo antes de a pessoa enxergar onde tinha parado. Aqui nao ha o
 * que recalcular: o inicializador do `useState` roda uma vez, e trocar de
 * conversa remonta tudo pelo `key={conversationId}` do chamador.
 */
function MessageHistory({
  me,
  messages,
  anchorRequest,
}: {
  readonly me: Me;
  /** Ja em ordem cronologica: o ultimo item da lista e a mensagem mais recente. */
  readonly messages: readonly Message[];
  readonly anchorRequest: number;
}) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLOListElement>(null);

  const [boundary] = useState(() => unreadBoundaryOf(messages, me.id));
  const entries = useMemo(
    () => threadEntries(messages, me.id, boundary),
    [messages, me.id, boundary],
  );

  /**
   * Ancora pendente. Nasce ligada — abrir a conversa cai na mensagem mais
   * recente — e so volta a ligar por pedido de quem esta lendo. Puxar para o fim
   * a cada consulta arrancaria do lugar quem subiu para reler, e e essa mesma
   * regra que vai deixar as paginas antigas serem prependidas sem roubar o lugar
   * de ninguem, quando o cursor real chegar.
   */
  const anchorPending = useRef(true);

  useEffect(() => {
    anchorPending.current = true;
  }, [anchorRequest]);

  useEffect(() => {
    const list = listRef.current;
    if (list === null || !anchorPending.current || entries.length === 0) return;
    anchorPending.current = false;
    // O ultimo filho e a mensagem mais recente porque a ordem foi decidida pela
    // tela — a resposta nao precisa chegar ordenada para isto valer.
    list.lastElementChild?.scrollIntoView({ block: 'end' });
  }, [entries]);

  return (
    <ol className={styles.messageList} ref={listRef}>
      {entries.map((entry) => (
        <Fragment key={entry.message.id}>
          {entry.opensUnread ? (
            // Item da lista, e nao `role="separator"`: trocar o papel tiraria o
            // `listitem` de dentro do `ol` e quebraria a propria lista para quem
            // le por tecnologia assistiva.
            <li className={styles.unreadMark}>
              {/*
                Chave propria, e nao a contagem da barra lateral: la "2 nao
                lidas" e um numero ao lado do nome; aqui e uma linha no meio do
                historico, e o que ela precisa dizer e **onde** a leitura parou.
                O mesmo texto nos dois lugares fazia o separador parecer mais um
                selo de contagem, perdido entre as mensagens.
              */}
              <span>{t('messaging.unreadDivider', { count: boundary.total })}</span>
            </li>
          ) : null}
          <MessageRow me={me} message={entry.message} side={entry.side} />
        </Fragment>
      ))}
    </ol>
  );
}

function MessageRow({
  me,
  message,
  side,
}: {
  readonly me: Me;
  readonly message: Message;
  readonly side: Side;
}) {
  const { t } = useTranslation();
  const download = useCreateAttachmentDownloadURL();

  return (
    // O lado vai para o DOM, e nao para duas classes escolhidas por um ternario:
    // a folha de estilo seleciona por `[data-side]`, entao a distincao visual e
    // consequencia direta do dado, sem um segundo lugar onde errar.
    <li className={styles.message} data-side={side} data-hidden={message.hiddenAt === null ? undefined : 'true'}>
      {/*
        Ocultada por moderacao. **Ocultar nao e apagar**: o registro permanece,
        e quem o ve precisa ver que ele foi ocultado — sem isso a linha e
        identica as demais e a moderacao fica invisivel justamente para o unico
        papel a quem o servidor manda estes campos.

        Em texto, e nao so em cor: cor sozinha nao chega a quem le por
        tecnologia assistiva nem a quem nao distingue a diferenca.

        Nao ha decisao de papel a tomar aqui. `hiddenBy` e `hiddenReason` so
        chegam preenchidos ao Super Admin — para os participantes a mensagem
        oculta nem aparece na lista —, entao a checagem de nulo **e** o
        controle de acesso, e repeti-la como `role === 'super_admin'` seria um
        segundo lugar onde errar.
      */}
      {message.hiddenAt === null ? null : (
        <p className={styles.moderated}>
          <span>{t('messaging.hidden')}</span>
          {message.hiddenBy ? (
            <span>{t('messaging.hiddenBy', { name: message.hiddenBy.displayName })}</span>
          ) : null}
          {message.hiddenReason ? (
            <span>{t('messaging.hiddenReason', { reason: message.hiddenReason })}</span>
          ) : null}
        </p>
      )}
      <p className={styles.meta}>
        <span>{message.sender.displayName}</span>
        <span className={styles.separator} aria-hidden="true">
          {' · '}
        </span>
        <span>
          {formatDateInPersonTimezone(message.sentAt, me.timezone, me.locale)}
          {' '}
          {formatTimeInPersonTimezone(message.sentAt, me.timezone, me.locale)}
        </span>
      </p>
      <p className={styles.body}>{message.body}</p>
      {message.attachments.length > 0 ? (
        <ul className={styles.attachmentList}>
          {message.attachments.map((attachment) => (
            <li key={attachment.id}>
              <Button
                variant="ghost"
                loading={download.isPending}
                onClick={() =>
                  download.mutate(
                    { attachmentId: attachment.id },
                    {
                      onSuccess: (signed) => {
                        // URL assinada de curta duracao: usada na hora e
                        // descartada, nunca guardada em estado nem em cache.
                        window.open(signed.url, '_blank', 'noopener,noreferrer');
                      },
                    },
                  )
                }
              >
                {t('messaging.downloadAttachment', { title: attachment.title })}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * Composicao de anexo.
 *
 * A politica e lida **antes** de qualquer escolha de arquivo, porque a
 * alternativa e deixar a pessoa escolher, tentar o upload e comer um 503 — que
 * e descobrir tarde demais. Enquanto a politica for provisoria, o botao fica
 * desabilitado com o motivo escrito.
 *
 * Nao ha campo de nome de arquivo original em lugar nenhum: o objeto e nomeado
 * pelo servidor, e o titulo e texto que o remetente escreve.
 */
function AttachmentComposer({
  locale,
  blocked,
  attached,
  onAttached,
}: {
  readonly locale: string;
  readonly blocked: boolean;
  readonly attached: readonly Attachment[];
  readonly onAttached: (attachment: Attachment) => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const fileId = useId();
  const policyQuery = useGetAttachmentPolicy();
  const upload = useCreateAttachmentUpload();

  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const policy = policyQuery.data;
  // Sem politica lida, trata como provisoria: o conservador aqui e recusar.
  const provisional = policy?.provisional !== false;
  const maxMegabytes = policy ? formatNumber(policy.maxSizeBytes / 1048576, locale, { maximumFractionDigits: 1 }) : '';

  const validate = (): string | null => {
    if (!policy) return t('messaging.attachmentPolicyUnknown');
    if (!title.trim()) return t('messaging.attachmentTitleRequired');
    if (!file) return t('messaging.attachmentPickFile');
    if (!policy.allowedMimeTypes.includes(file.type)) {
      return t('messaging.attachmentTypeRejected', { types: policy.allowedMimeTypes.join(', ') });
    }
    if (file.size > policy.maxSizeBytes) {
      return t('messaging.attachmentTooLarge', { max: maxMegabytes });
    }
    return null;
  };

  const attach = (): void => {
    const problem = validate();
    if (problem !== null || !file) {
      setError(problem);
      return;
    }
    setError(null);

    upload.mutate(
      { data: { title: title.trim(), mimeType: file.type, sizeBytes: file.size } },
      {
        onSuccess: async (result) => {
          try {
            await uploadToSignedUrl(result.upload.url, file);
            onAttached(result.attachment);
            setTitle('');
            setFile(null);
          } catch {
            setError(t('messaging.attachmentUploadFailed'));
          }
        },
        onError: (failure) => {
          setError(
            isProblemError(failure) && failure.status === 503
              ? t('messaging.attachmentProvisional')
              : t('error.unexpected'),
          );
        },
      },
    );
  };

  return (
    <fieldset className={styles.attachment}>
      <legend>{t('messaging.attachmentLegend')}</legend>

      <label className={styles.label} htmlFor={titleId}>
        {t('messaging.attachmentTitle')}
      </label>
      <input
        id={titleId}
        className={styles.input}
        type="text"
        value={title}
        disabled={blocked || provisional}
        maxLength={ATTACHMENT_TITLE_MAX_LENGTH}
        onChange={(event) => setTitle(event.target.value)}
      />

      <label className={styles.label} htmlFor={fileId}>
        {t('messaging.attachmentFile')}
      </label>
      <input
        id={fileId}
        className={styles.input}
        type="file"
        disabled={blocked || provisional}
        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
      />

      {policy && !provisional ? (
        <p className={styles.meta}>
          {t('messaging.attachmentRules', {
            types: policy.allowedMimeTypes.join(', '),
            max: maxMegabytes,
          })}
        </p>
      ) : null}

      <Button
        variant="secondary"
        disabled={blocked || provisional}
        loading={upload.isPending}
        onClick={attach}
      >
        {t('messaging.attach')}
      </Button>

      {provisional ? <p className={styles.reason}>{t('messaging.attachmentProvisional')}</p> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      {attached.length > 0 ? (
        <ul className={styles.attachmentList}>
          {attached.map((attachment) => (
            <li key={attachment.id}>{attachment.title}</li>
          ))}
        </ul>
      ) : null}
    </fieldset>
  );
}
